const VISITOR_KEY = "funnelIngest_visitorId";

function getOrCreateVisitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = `v_${crypto.randomUUID?.() || String(Math.random()).slice(2)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return `v_${Date.now()}`;
  }
}

function parseUtm(search) {
  const params = new URLSearchParams(search || "");
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  const out = {};
  for (const k of keys) {
    const v = params.get(k);
    if (v) out[k] = v;
  }
  return out;
}

function elementMatchesSelectors(el, selectors) {
  if (!el || !selectors?.length) return false;
  for (const sel of selectors) {
    try {
      if (sel && el.matches?.(sel)) return true;
    } catch {
      /* invalid */
    }
  }
  return false;
}

function clickSummary(target) {
  const tag = target.tagName?.toLowerCase() || "unknown";
  const id = target.id ? `#${target.id}` : "";
  const cls =
    typeof target.className === "string" && target.className
      ? `.${target.className.trim().split(/\s+/).slice(0, 3).join(".")}`
      : "";
  const text = (target.innerText || target.textContent || "").trim().slice(0, 80);
  return { tag, id, cls, text };
}

/**
 * @param {"explicit" | "auto"} mode
 */
function buildPayload(userConfig, body) {
  const { siteId, assignmentMode } = userConfig;
  const explicit =
    assignmentMode === "explicit" || (!!siteId && assignmentMode !== "auto");
  if (explicit) {
    return { siteId, ...body };
  }
  return { assignmentMode: "auto", ...body };
}

async function postIngest(userConfig, body) {
  const { ingestUrl, ingestSecret, debug } = userConfig;
  const payload = buildPayload(userConfig, body);
  const res = await fetch(ingestUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Ingest-Secret": ingestSecret,
    },
    body: JSON.stringify(payload),
    mode: "cors",
    keepalive: true,
  });
  const text = await res.text();
  if (!res.ok) {
    if (debug) {
      console.warn("[IngestTracker]", res.status, text);
    } else {
      console.warn("[IngestTracker]", res.status, text.slice(0, 200));
    }
    return;
  }
  if (debug && text.trim()) {
    try {
      console.log("[IngestTracker]", JSON.parse(text));
    } catch {
      console.log("[IngestTracker]", text);
    }
  }
}

/**
 * Server-ingest funnel tracker (no Firebase in the page).
 * - **Auto**: omit `siteId`, set `assignmentMode: "auto"`, use `FUNNEL_GLOBAL_INGEST_SECRET` as `ingestSecret`.
 * - **Explicit**: `siteId` + per-site `ingestSecret`.
 * @param {Record<string, unknown>} userConfig
 */
export async function initIngestTracker(userConfig) {
  const {
    ingestUrl,
    siteId = null,
    ingestSecret,
    assignmentMode,
    stage = "unknown",
    pageId = null,
    optInSelectors = [],
    salesSelectors = [],
    debug = false,
  } = userConfig;

  if (!ingestUrl || !ingestSecret) {
    console.error("[IngestTracker] ingestUrl and ingestSecret are required");
    return;
  }

  const explicit =
    assignmentMode === "explicit" || (!!siteId && assignmentMode !== "auto");

  if (explicit && !siteId) {
    console.error("[IngestTracker] siteId is required when using explicit assignment");
    return;
  }

  const cfg = { ...userConfig, debug };

  const visitorId = getOrCreateVisitorId();
  const href = window.location.href;
  const path = window.location.pathname + window.location.search;
  const referrer = document.referrer || null;
  const utm = parseUtm(window.location.search);
  const userAgent = navigator.userAgent;
  const resolvedPageId =
    pageId ||
    (() => {
      try {
        return btoa(unescape(encodeURIComponent(path))).slice(0, 48);
      } catch {
        return path.slice(0, 120);
      }
    })();

  const base = {
    stage,
    pageId: resolvedPageId,
    visitorId,
    href,
    path,
    referrer,
    utm,
    userAgent,
  };

  await postIngest(cfg, { type: "page_view", ...base });

  document.addEventListener(
    "click",
    (ev) => {
      const target = ev.target instanceof Element ? ev.target : null;
      if (!target) return;
      const clickable = target.closest?.("a,button,[role='button'],input[type='submit']");
      if (!clickable) return;

      const sum = clickSummary(clickable);
      const isOptIn = elementMatchesSelectors(clickable, optInSelectors);
      const isSales = elementMatchesSelectors(clickable, salesSelectors);
      let clickType = "click";
      if (isOptIn) clickType = "optin_click";
      else if (isSales) clickType = "sales_click";

      void postIngest(cfg, {
        type: clickType,
        ...base,
        href: window.location.href,
        path: window.location.pathname + window.location.search,
        click: sum,
        clickSelectorGuess: isOptIn ? "optInSelectors" : isSales ? "salesSelectors" : "generic",
      });
    },
    true,
  );
}
